import { useTranslation } from '@/src/i18n';
import { BlogPost } from '@/src/domain/models/BlogPost';
import { Ionicons } from '@expo/vector-icons';
import { useEffect, useState } from 'react';
import { Image, Pressable, ScrollView, Text, TouchableOpacity, useWindowDimensions, View } from 'react-native';
import { createStyles } from './LandingScreen.styles';

type ScreenshotItem = {
  labelKey: string;
  titleKey: string;
  descKey: string;
  color: string;
  image: number;
  imageDark: number;
};

export interface LandingScreenProps {
  isDark: boolean;
  onToggleTheme: () => void;
  onGetStarted: () => void;
  onSignIn: () => void;
  onSubscribe: () => void;
  onPrivacy: () => void;
  onTerms: () => void;
  onAppStore: () => void;
  onGooglePlay: () => void;
  latestBlogPosts?: BlogPost[];
  onBlogPostPress?: (slug: string) => void;
  onBlogPress?: () => void;
  colors: {
    background: string;
    card: string;
    border: string;
    text: string;
    textSecondary: string;
    textTertiary: string;
    primary: string;
    primaryText: string;
    surface: string;
  };
}

const FEATURES = [
  { titleKey: 'bracketsTitle', descKey: 'bracketsDesc', icon: 'trophy' as const, lib: 'ionicons', color: '#FF6B35' },
  { titleKey: 'participantsTitle', descKey: 'participantsDesc', icon: 'people' as const, lib: 'ionicons', color: '#4ECDC4' },
  { titleKey: 'standingsTitle', descKey: 'standingsDesc', icon: 'bar-chart' as const, lib: 'ionicons', color: '#45B7D1' },
  { titleKey: 'socialTitle', descKey: 'socialDesc', icon: 'heart' as const, lib: 'ionicons', color: '#FF6B9D' },
  { titleKey: 'leaguesTitle', descKey: 'leaguesDesc', icon: 'star' as const, lib: 'ionicons', color: '#FFD700' },
  { titleKey: 'teamsTitle', descKey: 'teamsDesc', icon: 'shield-checkmark' as const, lib: 'ionicons', color: '#7C3AED' },
  { titleKey: 'exportTitle', descKey: 'exportDesc', icon: 'share-social' as const, lib: 'ionicons', color: '#06B6D4' },
  { titleKey: 'inviteTitle', descKey: 'inviteDesc', icon: 'qr-code' as const, lib: 'ionicons', color: '#10B981' },
  { titleKey: 'exploreTitle', descKey: 'exploreDesc', icon: 'compass' as const, lib: 'ionicons', color: '#F59E0B' },
  { titleKey: 'mobileTitle', descKey: 'mobileDesc', icon: 'phone-portrait' as const, lib: 'ionicons', color: '#A78BFA' },
];

const SCREENSHOTS = [
  {
    labelKey: 'tournamentList',
    titleKey: 'tournamentListTitle',
    descKey: 'tournamentListDesc',
    color: '#FF6B35',
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    image: require('@/assets/images/screenshots/light_explore.png'),
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    imageDark: require('@/assets/images/screenshots/dark_explore.png'),
  },
  {
    labelKey: 'bracketView',
    titleKey: 'bracketViewTitle',
    descKey: 'bracketViewDesc',
    color: '#4ECDC4',
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    image: require('@/assets/images/screenshots/light_bracket.png'),
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    imageDark: require('@/assets/images/screenshots/dark_bracket.png'),
  },
  {
    labelKey: 'leaderboard',
    titleKey: 'leaderboardTitle',
    descKey: 'leaderboardDesc',
    color: '#A78BFA',
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    image: require('@/assets/images/screenshots/light_leaderboard.png'),
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    imageDark: require('@/assets/images/screenshots/dark_leaderboard.png'),
  },
];

const SOCIAL_SCREENSHOTS = [
  {
    labelKey: 'challenges',
    titleKey: 'challengesTitle',
    descKey: 'challengesDesc',
    color: '#FF6B9D',
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    image: require('@/assets/images/screenshots/light_challenges.png'),
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    imageDark: require('@/assets/images/screenshots/dark_challenges.png'),
  },
  {
    labelKey: 'friends',
    titleKey: 'friendsTitle',
    descKey: 'friendsDesc',
    color: '#45B7D1',
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    image: require('@/assets/images/screenshots/light_friends.png'),
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    imageDark: require('@/assets/images/screenshots/dark_friends.png'),
  },
  {
    labelKey: 'feed',
    titleKey: 'feedTitle',
    descKey: 'feedDesc',
    color: '#2DD4A8',
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    image: require('@/assets/images/screenshots/light_feed.png'),
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    imageDark: require('@/assets/images/screenshots/dark_feed.png'),
  },
];

export const LandingScreen = ({
  isDark,
  onToggleTheme,
  onGetStarted,
  onSignIn,
  onSubscribe,
  onPrivacy,
  onTerms,
  onAppStore,
  onGooglePlay,
  latestBlogPosts,
  onBlogPostPress,
  onBlogPress,
  colors,
}: LandingScreenProps) => {
  const { t, language } = useTranslation();
  const { width } = useWindowDimensions();
  const isWide = width >= 768;
  const styles = createStyles(isWide);
  const [hoveredScreenshot, setHoveredScreenshot] = useState<string | null>(null);
  const [tooltipRow, setTooltipRow] = useState<string | null>(null);
  const [selectedScreenshot, setSelectedScreenshot] = useState<ScreenshotItem | null>(null);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelectedScreenshot(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const comparisonRows: { feature: string; free: string; pro: string; proTooltip?: string }[] = [
    { feature: t('subscription.comparison.activeTournaments'), free: '3', pro: t('subscription.unlimited') },
    { feature: t('subscription.comparison.participants'), free: '16', pro: '512' },
    { feature: t('subscription.comparison.tournamentTypes'), free: 'SE, RR', pro: t('subscription.comparison.allTypes'), proTooltip: t('subscription.comparison.allTypesDetail') },
    { feature: t('subscription.comparison.activeLeagues'), free: '1', pro: t('subscription.unlimited') },
    { feature: t('subscription.comparison.pdfExport'), free: '✗', pro: '✓' },
    { feature: t('subscription.comparison.ads'), free: t('common.yes'), pro: t('common.no') },
  ];

  return (
    <View style={{ flex: 1 }}>
    <ScrollView style={[styles.container, { backgroundColor: colors.background }]}>
      {/* ── Header ── */}
      <View style={[styles.header, { backgroundColor: colors.background, borderBottomColor: colors.border }]}>
        <View style={styles.headerLogoContainer}>
          <Image source={require('@/assets/images/android-icon-foreground.png')} style={styles.headerLogoIcon} />
          <Text style={[styles.headerLogo, { color: colors.primary }]}>Tournaly</Text>
        </View>
        <View style={styles.headerActions}>
          <TouchableOpacity style={styles.headerThemeToggle} onPress={onToggleTheme}>
            <Ionicons name={isDark ? 'sunny' : 'moon'} size={20} color={colors.text} />
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.headerSignIn, { borderColor: colors.primary }]}
            onPress={onSignIn}
          >
            <Text style={[styles.headerSignInText, { color: colors.primary }]}>{t('landing.signIn')}</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* ── Hero ── */}
      <View style={styles.heroSection}>
        <Text style={[styles.heroTitle, { color: colors.text }]}>
          {t('landing.heroTitle') + '\n'}
          <Text style={[styles.heroTitleAccent, { color: colors.primary }]}>{t('landing.heroTitleAccent')}</Text>
        </Text>
        <Text style={[styles.heroTagline, { color: colors.textSecondary }]}>
          {t('landing.tagline')}
        </Text>
        <View style={styles.heroButtons}>
          <TouchableOpacity
            style={[styles.heroPrimaryBtn, { backgroundColor: colors.primary }]}
            onPress={onGetStarted}
          >
            <Text style={[styles.heroPrimaryBtnText, { color: colors.primaryText }]}>
              {t('landing.getStartedFree')}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.heroSecondaryBtn, { borderColor: colors.border }]}
            onPress={onSignIn}
          >
            <Text style={[styles.heroSecondaryBtnText, { color: colors.text }]}>
              {t('landing.signIn')}
            </Text>
          </TouchableOpacity>
        </View>

        {/* ── App store badges ── */}
        <View style={styles.appStoreRow}>
          <Text style={[styles.appStoreLabel, { color: colors.textTertiary }]}>
            {t('landing.downloadApp')}
          </Text>
          <View style={styles.appStoreBadges}>
            <TouchableOpacity
              style={[styles.appStoreBadge, { backgroundColor: colors.text }]}
              onPress={onAppStore}
            >
              <Ionicons name="logo-apple" size={18} color={colors.background} />
              <View>
                <Text style={[styles.appStoreBadgeTop, { color: colors.background }]}>
                  {t('landing.downloadOnAppStore')}
                </Text>
              </View>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.appStoreBadge, { backgroundColor: colors.text }]}
              onPress={onGooglePlay}
            >
              <Ionicons name="logo-google-playstore" size={18} color={colors.background} />
              <View>
                <Text style={[styles.appStoreBadgeTop, { color: colors.background }]}>
                  {t('landing.getItOnGooglePlay')}
                </Text>
              </View>
            </TouchableOpacity>
          </View>
        </View>
      </View>

      {/* ── Features ── */}
      <View style={[styles.section, { backgroundColor: colors.surface }]}>
        <Text style={[styles.sectionTitle, { color: colors.text }]}>{t('landing.features.title')}</Text>
        <View style={styles.featuresGrid}>
          {FEATURES.map((f) => (
            <View key={f.titleKey} style={[styles.featureCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
              <View style={[styles.featureIcon, { backgroundColor: f.color + '20' }]}>
                <Ionicons name={f.icon} size={24} color={f.color} />
              </View>
              <Text style={[styles.featureTitle, { color: colors.text }]}>
                {t(`landing.features.${f.titleKey}`)}
              </Text>
              <Text style={[styles.featureDesc, { color: colors.textSecondary }]}>
                {t(`landing.features.${f.descKey}`)}
              </Text>
            </View>
          ))}
        </View>
      </View>

      {/* ── Screenshots ── */}
      <View style={styles.section}>
        <Text style={[styles.sectionTitle, { color: colors.text }]}>{t('landing.screenshots.title')}</Text>

        {/* Tournament features row */}
        <View style={styles.screenshotsGroup}>
          <View style={styles.screenshotsRow}>
            {SCREENSHOTS.map((s) => {
              const isHovered = hoveredScreenshot === s.labelKey;
              return (
                <Pressable
                  key={s.labelKey}
                  style={styles.screenshotCard}
                  onHoverIn={() => setHoveredScreenshot(s.labelKey)}
                  onHoverOut={() => setHoveredScreenshot(null)}
                  onPress={() => setSelectedScreenshot(s)}
                >
                  <View style={[
                    styles.phoneFrame,
                    { borderColor: isHovered ? s.color : colors.border },
                    isHovered && styles.phoneFrameHovered,
                  ]}>
                    <Image
                      source={isDark ? s.imageDark : s.image}
                      style={{ width: '100%', height: '100%' }}
                      resizeMode="cover"
                      accessibilityLabel={t(`landing.screenshots.${s.labelKey}`)}
                    />
                    <View style={[styles.screenshotBadge, { backgroundColor: s.color }]} />
                  </View>
                  <View style={styles.screenshotCaption}>
                    <Text style={[styles.screenshotCaptionTitle, { color: colors.text }]}>
                      {t(`landing.screenshots.${s.titleKey}`)}
                    </Text>
                    <Text style={[styles.screenshotCaptionDesc, { color: colors.textSecondary }]}>
                      {t(`landing.screenshots.${s.descKey}`)}
                    </Text>
                  </View>
                </Pressable>
              );
            })}
          </View>
        </View>

        {/* Social features row */}
        <View style={styles.screenshotsGroup}>
          <Text style={[styles.screenshotsGroupTitle, { color: colors.textSecondary }]}>
            {t('landing.screenshots.socialSubtitle')}
          </Text>
          <View style={styles.screenshotsRow}>
            {SOCIAL_SCREENSHOTS.map((s) => {
              const isHovered = hoveredScreenshot === s.labelKey;
              return (
                <Pressable
                  key={s.labelKey}
                  style={styles.screenshotCard}
                  onHoverIn={() => setHoveredScreenshot(s.labelKey)}
                  onHoverOut={() => setHoveredScreenshot(null)}
                  onPress={() => setSelectedScreenshot(s)}
                >
                  <View style={[
                    styles.phoneFrame,
                    { borderColor: isHovered ? s.color : colors.border },
                    isHovered && styles.phoneFrameHovered,
                  ]}>
                    <Image
                      source={isDark ? s.imageDark : s.image}
                      style={{ width: '100%', height: '100%' }}
                      resizeMode="cover"
                      accessibilityLabel={t(`landing.screenshots.${s.labelKey}`)}
                    />
                    <View style={[styles.screenshotBadge, { backgroundColor: s.color }]} />
                  </View>
                  <View style={styles.screenshotCaption}>
                    <Text style={[styles.screenshotCaptionTitle, { color: colors.text }]}>
                      {t(`landing.screenshots.${s.titleKey}`)}
                    </Text>
                    <Text style={[styles.screenshotCaptionDesc, { color: colors.textSecondary }]}>
                      {t(`landing.screenshots.${s.descKey}`)}
                    </Text>
                  </View>
                </Pressable>
              );
            })}
          </View>
        </View>
      </View>

      {/* ── Pricing ── */}
      <View style={[styles.section, { backgroundColor: colors.surface }]}>
        <Text style={[styles.sectionTitle, { color: colors.text }]}>{t('landing.pricing.title')}</Text>
        <View style={styles.pricingRow}>
          {/* Free plan */}
          <View style={[styles.pricingCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
            <Text style={[styles.pricingPlanName, { color: colors.text }]}>{t('subscription.free')}</Text>
            <Text style={[styles.pricingPrice, { color: colors.text }]}>€0</Text>
            <Text style={[styles.pricingPriceSub, { color: colors.textSecondary }]}>{t('landing.pricing.forever')}</Text>
            {comparisonRows.map((row) => (
              <View key={row.feature} style={[styles.pricingRow2, { borderBottomColor: colors.border }]}>
                <Text style={[styles.pricingFeatureText, { color: colors.textSecondary }]}>{row.feature}</Text>
                <Text style={[styles.pricingValueText, { color: colors.text }]}>{row.free}</Text>
              </View>
            ))}
            <TouchableOpacity
              style={[styles.pricingBtn, { backgroundColor: colors.border }]}
              onPress={onGetStarted}
            >
              <Text style={[styles.pricingBtnText, { color: colors.text }]}>{t('landing.getStartedFree')}</Text>
            </TouchableOpacity>
          </View>

          {/* Pro plan */}
          <View style={[styles.pricingCard, { backgroundColor: colors.card, borderColor: colors.primary, borderWidth: 2 }]}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <Text style={[styles.pricingPlanName, { color: colors.text }]}>{t('subscription.pro')}</Text>
              <View style={{ backgroundColor: '#FFD700', paddingHorizontal: 8, paddingVertical: 2, borderRadius: 8 }}>
                <Text style={{ fontSize: 11, fontWeight: '800', color: '#8B4513' }}>PRO</Text>
              </View>
            </View>
            <Text style={[styles.pricingPrice, { color: colors.primary }]}>€4,99</Text>
            <Text style={[styles.pricingPriceSub, { color: colors.textSecondary }]}>{t('subscription.monthlyPrice')}</Text>
            {comparisonRows.map((row) => (
              <View key={row.feature} style={[styles.pricingRow2, { borderBottomColor: colors.border }]}>
                <Text style={[styles.pricingFeatureText, { color: colors.textSecondary }]}>{row.feature}</Text>
                {row.proTooltip ? (
                  <View style={{ position: 'relative' }}>
                    <Pressable
                      style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}
                      onHoverIn={() => setTooltipRow(row.feature)}
                      onHoverOut={() => setTooltipRow(null)}
                    >
                      <Text style={[styles.pricingValueText, { color: colors.primary }]}>{row.pro}</Text>
                      <Ionicons name="information-circle-outline" size={14} color={colors.primary} />
                    </Pressable>
                    {tooltipRow === row.feature && (
                      <View style={{
                        position: 'absolute',
                        bottom: '110%' as any,
                        right: 0,
                        backgroundColor: colors.card,
                        borderWidth: 1,
                        borderColor: colors.border,
                        borderRadius: 8,
                        padding: 10,
                        zIndex: 100,
                        minWidth: 190,
                        shadowColor: '#000',
                        shadowOpacity: 0.12,
                        shadowRadius: 8,
                        shadowOffset: { width: 0, height: 2 },
                      }}>
                        <Text style={{ fontSize: 12, color: colors.text, lineHeight: 20 }}>{row.proTooltip}</Text>
                      </View>
                    )}
                  </View>
                ) : (
                  <Text style={[styles.pricingValueText, { color: colors.primary }]}>{row.pro}</Text>
                )}
              </View>
            ))}
            <TouchableOpacity
              style={[styles.pricingBtn, { backgroundColor: colors.primary }]}
              onPress={onSubscribe}
            >
              <Text style={[styles.pricingBtnText, { color: colors.primaryText }]}>{t('subscription.upgradeToPro')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>

      {/* ── Blog / What's New ── */}
      {latestBlogPosts && latestBlogPosts.length > 0 && (
        <View style={styles.section}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>{t('blog.latestUpdates')}</Text>
          <View style={styles.blogGrid}>
            {latestBlogPosts.map((post) => {
              const displayTitle = language === 'es' ? post.titleEs : post.title;
              const displaySummary = language === 'es' ? post.summaryEs : post.summary;
              const formatDate = (dateStr: string) =>
                new Date(dateStr + 'T00:00:00').toLocaleDateString(
                  language === 'es' ? 'es-ES' : 'en-US',
                  { year: 'numeric', month: 'long', day: 'numeric' },
                );
              return (
                <TouchableOpacity
                  key={post.slug}
                  style={[styles.blogCard, { backgroundColor: colors.card, borderColor: colors.border }]}
                  onPress={() => onBlogPostPress?.(post.slug)}
                  activeOpacity={0.8}
                >
                  <View style={styles.blogCardTags}>
                    {post.tags.map((tag) => (
                      <View key={tag} style={[styles.blogCardTag, { borderColor: colors.primary }]}>
                        <Text style={[styles.blogCardTagText, { color: colors.primary }]}>{tag}</Text>
                      </View>
                    ))}
                  </View>
                  <Text style={[styles.blogCardTitle, { color: colors.text }]} numberOfLines={2}>
                    {displayTitle}
                  </Text>
                  <Text style={[styles.blogCardDate, { color: colors.textTertiary }]}>
                    {formatDate(post.date)}
                  </Text>
                  <Text style={[styles.blogCardSummary, { color: colors.textSecondary }]} numberOfLines={3}>
                    {displaySummary}
                  </Text>
                  <Text style={[styles.blogCardReadMore, { color: colors.primary }]}>
                    {t('blog.readMore')}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
          <TouchableOpacity style={styles.blogViewAll} onPress={onBlogPress}>
            <Text style={[styles.blogViewAllText, { color: colors.primary }]}>
              {t('blog.viewAllPosts')}
            </Text>
          </TouchableOpacity>
        </View>
      )}

      {/* ── Footer CTA ── */}
      <View style={styles.footerSection}>
        <Text style={[styles.footerCta, { color: colors.text }]}>{t('landing.footer.cta')}</Text>
        <TouchableOpacity
          style={[styles.heroPrimaryBtn, { backgroundColor: colors.primary }]}
          onPress={onGetStarted}
        >
          <Text style={[styles.heroPrimaryBtnText, { color: colors.primaryText }]}>
            {t('landing.getStartedFree')}
          </Text>
        </TouchableOpacity>
        <View style={styles.footerLinks}>
          <TouchableOpacity onPress={onPrivacy}>
            <Text style={[styles.footerLink, { color: colors.textTertiary }]}>{t('landing.footer.privacy')}</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={onTerms}>
            <Text style={[styles.footerLink, { color: colors.textTertiary }]}>{t('landing.footer.terms')}</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={onBlogPress}>
            <Text style={[styles.footerLink, { color: colors.textTertiary }]}>{t('navigation.blog')}</Text>
          </TouchableOpacity>
        </View>
        <View style={styles.footerCopyrightRow}>
          <Image source={require('@/assets/images/android-icon-foreground.png')} style={styles.footerLogoIcon} />
          <Text style={[styles.footerCopyright, { color: colors.textTertiary }]}>
            © {new Date().getFullYear()} Tournaly. {t('landing.footer.copyright')}
          </Text>
        </View>
      </View>
    </ScrollView>

    {/* ── Lightbox ── */}
    {selectedScreenshot && (
      <Pressable style={styles.lightboxOverlay} onPress={() => setSelectedScreenshot(null)}>
        <Pressable style={styles.lightboxContent} onPress={() => {}}>
          <TouchableOpacity style={styles.lightboxClose} onPress={() => setSelectedScreenshot(null)}>
            <Ionicons name="close" size={28} color="white" />
          </TouchableOpacity>
          <View style={[styles.lightboxPhone, { borderColor: selectedScreenshot.color }]}>
            <Image
              source={isDark ? selectedScreenshot.imageDark : selectedScreenshot.image}
              style={{ width: '100%', height: '100%' }}
              resizeMode="cover"
            />
          </View>
          <View style={styles.lightboxCaption}>
            <Text style={styles.lightboxTitle}>
              {t(`landing.screenshots.${selectedScreenshot.titleKey}`)}
            </Text>
            <Text style={styles.lightboxDesc}>
              {t(`landing.screenshots.${selectedScreenshot.descKey}`)}
            </Text>
          </View>
        </Pressable>
      </Pressable>
    )}
    </View>
  );
};
